import { buildTerrain, buildRows, parcelFrame } from "./sceneRows";
import { localXY, tablesOrigin } from "./contextPlanes";
import { buildSiteGeometry, combineTriangles, roofSurfaces, siteGeometryErrors } from "./siteGeometry";
import { deriveField } from "./fieldGeometry";
import { layoutGeometry, moduleQuads, resolveRoofModuleOffsets } from "./moduleLayout";

// All saved surfaces share east/north/up metres. Legacy geographic layouts use
// their surveyed table centres; numeric layouts require an explicit origin.
export const sceneDatum = (geometry) => Number(geometry?.frame?.elevationM || 0) + Number(geometry?.roof?.heightM || 0);
export function sceneOffset(active, other) {
  const a = active.siteGeometry?.frame; const b = other.siteGeometry?.frame;
  let xy;
  if (a && b) xy = { x: Number(b.eastM) - Number(a.eastM), y: Number(b.northM) - Number(a.northM) };
  else if (!a && !b) xy = localXY(other.spacing?.parcel_outline?.frame_origin_lonlat || tablesOrigin(other.tables),
    active.origin || active.spacing?.parcel_outline?.frame_origin_lonlat || tablesOrigin(active.tables));
  if (!xy || ![xy.x, xy.y].every(Number.isFinite)) return null;
  return { ...xy, z: sceneDatum(other.siteGeometry) - sceneDatum(active.siteGeometry) };
}

export function translateMesh(mesh, offset) {
  if (!mesh?.length) return null;
  return Float32Array.from(mesh, (v, i) => v + [offset.x, offset.y, offset.z][i % 3]);
}
export function rowsMesh(rows) {
  return new Float32Array((rows || []).flatMap((r) => {
    const a = r.low[0], b = r.low[1], c = r.high[1], d = r.high[0];
    return [a, b, c, a, c, d].flatMap((p) => [p.x, p.y, p.z]);
  }));
}

export function descriptorRows(plane) {
  const spacing = plane.spacing || {};
  const pitch = Number(spacing.minimum_distance_m ?? spacing.pitch_m);
  const tiers = Number(plane.tiers);
  if (!(pitch > 0 && tiers > 0 && plane.moduleWidth > 0 && plane.moduleAlongSlope > 0) ||
      !Number.isFinite(Number(plane.tilt)) || siteGeometryErrors(plane.siteGeometry).length) return [];
  const roof = plane.siteGeometry?.roof || (plane.onRoof
    ? { pitchDeg: plane.roofPitch, azimuthDeg: plane.roofAzimuth } : null);
  const terrain = buildTerrain(roof?.pitchDeg ?? (plane.onRoof ? plane.roofPitch : plane.slopeTilt),
    roof?.azimuthDeg ?? (plane.onRoof ? plane.roofAzimuth : plane.slopeAzimuth));
  const field = deriveField({ totalModules: plane.totalModules, pitch, moduleWidth: plane.moduleWidth,
    verticalModules: tiers, fieldRows: spacing.field_rows, rowModules: spacing.row_modules });
  const parcel = parcelFrame(plane.tables, spacing.parcel_outline);
  if (!field && !parcel) return [];
  const geo = buildRows({ axisAzimuth: Number(plane.azimuth) - 90, moduleWidth: plane.moduleWidth,
    pitch, collectorWidth: tiers * plane.moduleAlongSlope, clearance: Number(plane.clearance ?? 0.5),
    roof, terrain, parcel,
    arrayOffset: { eastM: Number(spacing.array_offset_east_m || 0), northM: Number(spacing.array_offset_north_m || 0) },
  }, Number(plane.tilt), parcel ? plane.tables.length : field.rowsUsed,
    field?.rowLength, null, field?.rowModules);
  const offsets = resolveRoofModuleOffsets(geo.rows, tiers, spacing, terrain.groundZ);
  // Slots taken out of the plan by hand hold no module in the shared scene
  // either - a neighbour face must not be shaded by a module that is not there.
  const excluded = Array.isArray(spacing.excluded_slots) && spacing.excluded_slots.length ? spacing.excluded_slots : null;
  return layoutGeometry(geo, tiers, offsets, terrain.groundZ, Boolean(excluded), excluded).rows;
}

/** Same triangles for the picture and ray casting, including other buildings. */
/**
 * Every roof face of the project, in the ACTIVE scene's frame: the active
 * scene's own, and each other plane's at its offset. Used to leave out the
 * walls between faces that meet (siteGeometry.buildSiteGeometry).
 */
export function projectRoofFaces(planes, active) {
  const out = roofSurfaces(active?.siteGeometry).map((f) => ({ ...f, owner: null }));
  for (const plane of planes || []) {
    const offset = sceneOffset(active, plane);
    if (!offset) continue;
    roofSurfaces(plane.siteGeometry, buildTerrain(plane.slopeTilt, plane.slopeAzimuth).groundZ, offset)
      .forEach((f) => out.push({ ...f, owner: plane.id }));
  }
  return out;
}

/** The same faces, moved into another plane's frame, leaving that plane's own out. */
function facesFor(allFaces, ownerId, offset) {
  return allFaces.filter((f) => f.owner !== ownerId).map((f) => ({
    ring: f.ring.map((p) => ({ x: p.x - offset.x, y: p.y - offset.y })),
    top: (p) => f.top({ x: p.x + offset.x, y: p.y + offset.y }) - offset.z,
  }));
}

export function buildProjectNeighbours(planes, active) {
  const rows = [], meshes = [], solids = [], closedSolids = [], missing = [], objects = [];
  const allFaces = projectRoofFaces(planes, active);
  for (const plane of planes || []) {
    const offset = sceneOffset(active, plane);
    const ownRows = descriptorRows(plane);
    const emptyRoof = Number(plane.totalModules) === 0 && (plane.onRoof || plane.siteGeometry?.roof);
    if (!offset || siteGeometryErrors(plane.siteGeometry).length || (!ownRows.length && !emptyRoof)) {
      missing.push(plane.label); continue;
    }
    if (plane.previewOnly) missing.push(plane.label);
    const shift = (p) => ({ x: p.x + offset.x, y: p.y + offset.y, z: p.z + offset.z });
    const placed = ownRows.map((r) => ({ ...r, label: plane.label, tiers: plane.tiers,
      low: r.low.map(shift), high: r.high.map(shift) }));
    rows.push(...placed);
    const terrain = buildTerrain(plane.slopeTilt, plane.slopeAzimuth);
    const site = buildSiteGeometry(plane.siteGeometry, terrain.groundZ, { otherFaces: facesFor(allFaces, plane.id, offset) });
    const solid = combineTriangles(translateMesh(site.roofMesh, offset), translateMesh(site.obstacles, offset));
    solids.push(solid);
    closedSolids.push(translateMesh(site.roofMesh, offset), translateMesh(site.solidObstacles, offset));
    meshes.push(rowsMesh(placed), solid);
    objects.push({ id: plane.id, label: plane.label, frame: plane.siteGeometry?.frame, previewOnly: plane.previewOnly,
      mesh: combineTriangles(rowsMesh(placed), solid),
      plane, roofMesh: translateMesh(site.roofMesh, offset), arrayMesh: rowsMesh(placed),
      modules: (ownRows.some((r) => r.slot) ? ownRows : moduleQuads(ownRows, plane.tiers)).map((r) => ({
        ...r, low: r.low.map(shift), high: r.high.map(shift),
      })),
      gradient: (() => {
        const roof = plane.siteGeometry?.roof;
        const t = buildTerrain(roof?.pitchDeg ?? plane.roofPitch ?? plane.slopeTilt,
          roof?.azimuthDeg ?? plane.roofAzimuth ?? plane.slopeAzimuth);
        return { x: t.groundZ(1, 0), y: t.groundZ(0, 1) };
      })(),
      obstacles: (plane.siteGeometry?.obstacles || []).map((obstacle) => ({
        ...obstacle, mesh: translateMesh(buildSiteGeometry({ ...plane.siteGeometry, obstacles: [obstacle] }, terrain.groundZ).obstacles, offset),
      })),
    });
  }
  return { rows, triangles: combineTriangles(...meshes), solids: combineTriangles(...solids), closedSolids: combineTriangles(...closedSolids), missing, objects };
}
